/**
 * Shared visual contract for rich HTML edit and view.
 * Web edit (Tiptap), native edit (10tap WKWebView), web view (real DOM),
 * and native view (`RICH_HTML_TAGS_STYLES`) must use these tokens.
 *
 * Body/placeholder match FormInput (`#333` / `#999` / 14px / 20px line).
 */

import { colors } from '../../theme/colors';
import { fontSize, lineHeight } from '../../theme/typography';

import type { TextStyle } from 'react-native';

export const RICH_HTML_BODY_COLOR = colors.textPrimary;
export const RICH_HTML_PLACEHOLDER_COLOR = colors.inputPlaceholder;
export const RICH_HTML_BODY_FONT_SIZE = fontSize.formControl;
export const RICH_HTML_BODY_LINE_HEIGHT = lineHeight.formControl;

/**
 * Extra gap below a block on top of the 20px line box. Tighter than the
 * legacy `0.5em`, but non-zero so a hard return still reads as a new block
 * rather than a wrapped line.
 */
export const RICH_HTML_BLOCK_SPACING = 4;
export const RICH_HTML_LIST_ITEM_SPACING = 4;
/** Headings still need air so they do not collide with the next block. */
export const RICH_HTML_HEADING_SPACING = 8;

export const RICH_HTML_IMG_MAX_WIDTH = 240;
export const RICH_HTML_IMG_MAX_HEIGHT = 160;
export const RICH_HTML_LINK_COLOR = '#2563EB';
export const RICH_HTML_MARK_BG = '#FEF08A';
export const RICH_HTML_TABLE_BORDER = '#D1D5DB';
export const RICH_HTML_TABLE_HEADER_BG = '#F3F4F6';

/**
 * A literal U+00A0, not the `\00a0` CSS escape. 10tap embeds each bridge's CSS
 * in a JS template literal (`getStyleSheetCSS`), where `\0` followed by a digit
 * is an illegal octal escape — one such sequence is a SyntaxError that throws
 * away *every* bridge stylesheet, leaving the native editor on UA defaults.
 */
const RICH_HTML_EMPTY_PARAGRAPH_CONTENT = '\u00a0';

/** CSS for one scoped root (`.ss-rich-html-view`, `.ProseMirror`, …). */
/**
 * The mention token's look (#1599 Story 1.4, AC 10): link colour at weight
 * 600, on the message view and in the web editor alike. A separate rule, NOT
 * part of `richHtmlScopedCss`, because that one also feeds the native editor's
 * bridge CSS and Story 3.1 owns the native token.
 *
 * #1599 Story 1.5 (UX-DR2, frame C1): the second rule tints the VIEWER's own
 * token, which `resolveMentionTokens` marks with `data-mention-viewer` after
 * the sanitizer. Higher specificity than the first rule, so it wins on the
 * same span. `mentionOwnText` on `mentionOwnGround` is 5.38:1 (pinned in
 * `colors.test.ts`); frame C1's own pair was 3.9:1 and failed SC 1.4.3. The
 * web editor's copy of these rules inherits the second one harmlessly: the
 * composer never carries `data-mention-viewer`.
 *
 * #1599 Story 1.7: the one collective token (`span[data-mention-group]`,
 * "@Vertiv Config Team") takes the same two rules; the view marks it
 * `data-mention-viewer` for a viewer who holds Configuration Engineer.
 */
export function mentionTokenCss(scope: string): string {
  return `${scope} span[data-mention-user-id], ${scope} span[data-mention-group] {
  color: ${colors.textLink};
  font-weight: 600;
}
${scope} span[data-mention-user-id][data-mention-viewer], ${scope} span[data-mention-group][data-mention-viewer] {
  background-color: ${colors.mentionOwnGround};
  color: ${colors.mentionOwnText};
  font-weight: 700;
  border-radius: 3px;
  padding: 0 3px;
}`;
}

/**
 * The same two looks for the NATIVE message view (#1599 Story 3.2), where
 * `mentionTokenCss` never arrives: react-native-render-html reads no
 * stylesheet, so its span renderer asks `mentionTokenNativeStyle` instead.
 * Kept beside the CSS so the two cannot drift apart.
 *
 * ⚠️ NO `borderRadius` AND NO PADDING: a token is a NESTED `Text`, and RN
 * nested text takes neither, so the native tint is the ground colour only
 * (ledgered, Story 1599-3.2).
 */
export const MENTION_TOKEN_NATIVE_STYLE: TextStyle = {
  color: colors.textLink,
  fontWeight: '600',
};

export const MENTION_OWN_TOKEN_NATIVE_STYLE: TextStyle = {
  color: colors.mentionOwnText,
  backgroundColor: colors.mentionOwnGround,
  fontWeight: '700',
};

/**
 * Is this span a mention token? A person or group id, from the attributes
 * `resolveMentionTokens` leaves after the sanitizer. The ONE predicate for
 * both native questions, the look (`mentionTokenNativeStyle`) and the spoken
 * label (`RichHtmlContent`'s `mentionSpokenLabel`), so they cannot disagree
 * (code review 2026-10-26; `correspondence.md`, two guards for one property).
 */
export function isMentionTokenSpan(
  attributes: Readonly<Record<string, string>>,
): boolean {
  return (
    attributes['data-mention-user-id'] !== undefined ||
    attributes['data-mention-group'] !== undefined
  );
}

/**
 * A span's token look from its POST-SANITIZE attributes, the ones
 * `resolveMentionTokens` writes: a person or group id means a token, and
 * `data-mention-viewer` means it is the viewer's own. `undefined` for any
 * other span (a departed or unresolved name is plain text and never gets
 * here). Never re-derives "you" from anything else.
 */
export function mentionTokenNativeStyle(
  attributes: Readonly<Record<string, string>>,
): TextStyle | undefined {
  if (!isMentionTokenSpan(attributes)) return undefined;
  return attributes['data-mention-viewer'] !== undefined
    ? { ...MENTION_TOKEN_NATIVE_STYLE, ...MENTION_OWN_TOKEN_NATIVE_STYLE }
    : MENTION_TOKEN_NATIVE_STYLE;
}

/** The class on a token the server refused (#1599 Story 1.4, AC 9). */
export const MENTION_REFUSED_CLASS = 'ss-mention-refused';

/**
 * A refused token's outline (#1599 Story 1.4, AC 9): badge red with a 2px
 * underline. Extracted from `RichTextEditor.web.tsx`'s editor CSS by #1599
 * Story 3.1 so the native editor's bridge CSS (`nativeMentionBridge.ts`)
 * carries the same rule; the web output is byte-identical (pinned in
 * `rich-html-visual-contract.test.ts`). The class is set by the refusal
 * decoration in `siteScopeMention.ts`, on both editors.
 */
export function mentionRefusedCss(scope: string): string {
  return `${scope} .${MENTION_REFUSED_CLASS} {
  color: ${colors.unreadBadge};
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;
}`;
}

export function richHtmlScopedCss(scope: string): string {
  return `
${scope} {
  color: ${RICH_HTML_BODY_COLOR};
  font-size: ${RICH_HTML_BODY_FONT_SIZE}px;
  line-height: ${RICH_HTML_BODY_LINE_HEIGHT}px;
}
${scope} p {
  margin: 0 0 ${RICH_HTML_BLOCK_SPACING}px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}
${scope} p:empty:not(.is-editor-empty)::before {
  content: '${RICH_HTML_EMPTY_PARAGRAPH_CONTENT}';
}
${scope} h1 {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 ${RICH_HTML_HEADING_SPACING}px;
}
${scope} h2 {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 ${RICH_HTML_HEADING_SPACING}px;
}
${scope} h3 {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 ${RICH_HTML_HEADING_SPACING}px;
}
${scope} a {
  color: ${RICH_HTML_LINK_COLOR};
  text-decoration: underline;
}
${scope} mark {
  background-color: ${RICH_HTML_MARK_BG};
  padding: 0 2px;
  border-radius: 2px;
}
${scope} img {
  max-width: ${RICH_HTML_IMG_MAX_WIDTH}px;
  max-height: ${RICH_HTML_IMG_MAX_HEIGHT}px;
  width: auto;
  height: auto;
  display: inline-block;
  vertical-align: top;
  object-fit: contain;
}
${scope} ul,
${scope} ol {
  margin: 0 0 ${RICH_HTML_BLOCK_SPACING}px;
  padding-left: 1.5em;
}
${scope} li {
  margin: 0 0 ${RICH_HTML_LIST_ITEM_SPACING}px;
}
${scope} li > p,
${scope} li > div {
  display: inline;
  margin: 0;
}
${scope} table {
  border-collapse: collapse;
  margin: 8px 0;
  width: max-content;
  max-width: none;
  table-layout: auto;
  word-break: normal;
  overflow-wrap: normal;
}
${scope} td,
${scope} th {
  border: none;
  min-width: 80px;
  padding: 6px 8px;
  vertical-align: top;
  word-break: normal;
  overflow-wrap: normal;
}
${scope} table[data-ss-grid] td,
${scope} table[data-ss-grid] th {
  border: 1px solid #D1D5DB;
}
${scope} td p,
${scope} th p {
  margin: 0;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
}
${scope} th {
  background: ${RICH_HTML_TABLE_HEADER_BG};
  font-weight: 700;
}
${scope} pre {
  background: #f3f4f6;
  color: #111827;
  border-radius: 4px;
  padding: 8px;
  font-family: monospace;
  white-space: pre-wrap;
}
${scope} code {
  font-family: monospace;
  background: #F3F4F6;
  border-radius: 3px;
  padding: 1px 4px;
}
${scope} blockquote {
  border-left: 3px solid #9CA3AF;
  padding-left: 10px;
  margin: 0 0 ${RICH_HTML_BLOCK_SPACING}px;
}
`;
}
